import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, from } from 'rxjs';
import { AuthService } from './auth.service';
import { ApiService } from './api.service';
import { switchMap } from 'rxjs/operators';

/**
 * Service for managing tenant selection in multi-tenant scenarios.
 * Stores selected tenant ID and provides observables for tenant changes.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantSelectionService {
  private authService = inject(AuthService);
  private apiService = inject(ApiService);
  private injector = inject(Injector);
  private readonly STORAGE_KEY = 'crowdctrl-selected-tenant-id';
  private baseUrl: string = '';

  private selectedTenantIdSubject = new BehaviorSubject<string | null>(null);
  public selectedTenantId$: Observable<string | null> = this.selectedTenantIdSubject.asObservable();

  constructor() {
    // Load selected tenant from storage on initialization
    this.loadSelectedTenant();
  }

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get the currently selected tenant ID
   */
  getSelectedTenantId(): string | null {
    return this.selectedTenantIdSubject.value;
  }

  /**
   * Set the selected tenant ID
   * Activates tenant on backend and updates local storage
   * No waiting for token propagation - tenantId is passed in requests
   */
  async setSelectedTenantId(tenantId: string): Promise<void> {
    // Set tenant ID in subject and localStorage FIRST so API calls can use it
    // This is needed because activateTenant makes an API call that requires a tenant ID
    localStorage.setItem(this.STORAGE_KEY, tenantId);
    this.selectedTenantIdSubject.next(tenantId);
    // Now activate tenant (this API call will have the tenant ID available)
    await this.activateTenant(tenantId);
    this.authService.clearTokenCache();
  }

  /**
   * Clear the selected tenant ID
   */
  clearSelectedTenant(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.selectedTenantIdSubject.next(null);
  }

  /**
   * Get available tenant IDs from custom claims
   * Returns null if user is not multi-tenant
   * Uses cached token result when possible (30-second TTL)
   */
  async getAvailableTenants(forceRefresh: boolean = false): Promise<string[] | null> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return null;
    }

    try {
      // Use AuthService's cached token result method
      const tokenResult = await this.authService.getTokenResult(forceRefresh);
      if (!tokenResult) {
        return null;
      }

      const claims = tokenResult.claims;
      const isMultiTenant = claims['multiTenant'] === true;
      const tenantIds = Array.isArray(claims['tenantIds'])
        ? (claims['tenantIds'] as string[])
        : null;

      // Check if user is multi-tenant
      if (isMultiTenant && tenantIds) {
        return tenantIds;
      }

      // Single tenant - return tenantId if available
      if (claims['tenantId']) {
        return [claims['tenantId'] as string];
      }

      return null;
    } catch (error) {
      console.error('Error getting available tenants:', error);
      return null;
    }
  }

  /**
   * Check if user is multi-tenant
   * Uses cached token result when possible (30-second TTL)
   */
  async isMultiTenant(forceRefresh: boolean = false): Promise<boolean> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return false;
    }

    try {
      // Use AuthService's cached token result method
      const tokenResult = await this.authService.getTokenResult(forceRefresh);
      if (!tokenResult) {
        return false;
      }

      const claims = tokenResult.claims;
      return claims['multiTenant'] === true;
    } catch (error) {
      console.error('Error checking multi-tenant status:', error);
      return false;
    }
  }

  /**
   * Get tenant ID from custom claims (for single-tenant users)
   * Uses cached token result when possible (30-second TTL)
   */
  async getTenantIdFromClaims(forceRefresh: boolean = false): Promise<string | null> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return null;
    }

    try {
      // Use AuthService's cached token result method
      const tokenResult = await this.authService.getTokenResult(forceRefresh);
      if (!tokenResult) {
        return null;
      }

      const claims = tokenResult.claims;
      return claims['tenantId'] as string | null;
    } catch (error) {
      console.error('Error getting tenant ID from claims:', error);
      return null;
    }
  }

  /**
   * Get the effective tenant ID to use for API calls
   * Returns selected tenant for multi-tenant users, or tenantId from claims for single-tenant users
   * Validates that the selected tenant is still in the user's available tenants
   * If stored tenant exists but token claims are stale, refreshes token to sync claims
   */
  async getEffectiveTenantId(): Promise<string | null> {
    const tenantIds = await this.getAvailableTenants();
    const storedTenant = this.selectedTenantIdSubject.value;

    // Priority 1: Use stored tenant if it exists and is valid
    if (storedTenant && tenantIds?.includes(storedTenant)) {
      // Get token with tenant validation (this will invalidate cache if tenant doesn't match)
      let tokenResult = await this.authService.getTokenResult(false, storedTenant);
      let claimTenantId = tokenResult?.claims['tenantId'] as string | null;

      // If token matches stored tenant, we're good
      if (claimTenantId === storedTenant) {
        return storedTenant;
      }

      // Token doesn't match - force refresh with tenant validation
      tokenResult = await this.authService.getTokenResult(true, storedTenant);
      claimTenantId = tokenResult?.claims['tenantId'] as string | null;

      if (claimTenantId === storedTenant) {
        return storedTenant;
      }

      // Still doesn't match - for multi-tenant users, activate to sync claims
      const isMulti = await this.isMultiTenant();
      if (isMulti) {
        // Activate tenant without full setSelectedTenantId flow to avoid recursion
        await this.activateTenant(storedTenant);
        this.authService.clearTokenCache();
        // Try one more time to get token with correct tenant
        const finalTokenResult = await this.authService.getTokenResult(true, storedTenant);
        const finalClaimTenantId = finalTokenResult?.claims['tenantId'] as string | null;
        if (finalClaimTenantId === storedTenant) {
          return storedTenant;
        }
        // If still doesn't match, return stored tenant anyway
        // The activation will eventually propagate
        return storedTenant;
      }

      // Single tenant - token is authoritative, update stored to match
      if (claimTenantId && tenantIds.includes(claimTenantId)) {
        this.selectedTenantIdSubject.next(claimTenantId);
        localStorage.setItem(this.STORAGE_KEY, claimTenantId);
        return claimTenantId;
      }
    }

    // Priority 2: Use tenant from claims (for single-tenant or when no stored tenant)
    const tokenResult = await this.authService.getTokenResult();
    const claimTenantId = tokenResult?.claims['tenantId'] as string | null;

    if (claimTenantId && tenantIds?.includes(claimTenantId)) {
      // Update local state to match claims
      this.selectedTenantIdSubject.next(claimTenantId);
      localStorage.setItem(this.STORAGE_KEY, claimTenantId);
      return claimTenantId;
    }

    return null;
  }

  /**
   * Ensure a tenant is selected (last selected or first available for multi-tenant users)
   * Activates tenant to refresh claims when needed.
   */
  async ensureSelectedTenant(availableTenantIds?: string[] | null): Promise<string | null> {
    const tenantIds = availableTenantIds ?? (await this.getAvailableTenants());
    if (!tenantIds || tenantIds.length === 0) {
      return null;
    }

    const isMultiTenant = await this.isMultiTenant();
    const stored = this.selectedTenantIdSubject.value;
    const target = stored && tenantIds.includes(stored) ? stored : tenantIds[0];

    if (!target) {
      return null;
    }

    if (!isMultiTenant) {
      // Single-tenant users rely on claims; just mirror selection locally.
      this.selectedTenantIdSubject.next(target);
      localStorage.setItem(this.STORAGE_KEY, target);
      return target;
    }

    const claimTenantId = await this.getTenantIdFromClaims();
    if (claimTenantId !== target) {
      await this.setSelectedTenantId(target);
    } else if (this.selectedTenantIdSubject.value !== target) {
      this.selectedTenantIdSubject.next(target);
      localStorage.setItem(this.STORAGE_KEY, target);
    }

    return target;
  }

  /**
   * Load selected tenant from localStorage
   */
  private loadSelectedTenant(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      this.selectedTenantIdSubject.next(stored);
    }
  }

  private async activateTenant(tenantId: string): Promise<void> {
    await firstValueFrom(
      from(
        this.apiService.post<{ success: boolean }, { tenantId: string }>(
          this.baseUrl,
          '/tenant_selection_api/api/active-tenant',
          { tenantId },
        ),
      ).pipe(switchMap((obs) => obs)),
    );
  }
}
